import { Card, Form, notification } from "antd";
import useCurrencyTableColumns from "./hooks/useCurrencyTableColumns";
import { AlertMessage, TableFilter } from "@neonumy/ant-design";
import { useLocation, useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import CreateCurrency from "./components/CreateCurrency";
import EditableCell from "./components/EditableCell";
import useCoreStore from "views/hooks/useCoreStore";
import useCurrencyApi from "./hooks/useCurrencyApi";
import { ApolloQueryResult } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { FaCoins } from "react-icons/fa";
import CardTitle from "components/CardTitle";

const paginationInit = {
  offset: 0,
  limit: 10,
};

interface CurrencyPropsTypes {
  isSetup?: boolean;
  loading?: boolean;
  refetch?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetCompanySettingResponse>>;
}

export default function Currency(props: CurrencyPropsTypes): React.ReactElement {
  const { isSetup, refetch: refetchSettings } = props;
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const [form] = Form.useForm();
  const location = useLocation();
  const tabName = location.pathname.split("/").pop();

  const decimalPlaces = Form.useWatch("decimalPlaces", form);
  const isMainCurrency = Form.useWatch("isMainCurrency", form);
  const isActive = Form.useWatch("isActive", form);
  const currency = useCoreStore(state => state.currency);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingKey, setEditingKey] = useState<ID>();
  const { notificationSuccessfullySaved } = useNotification();

  const {
    currenciesData: dataSource,
    listCurrencyTableData,
    totalCount,
    disableCurrencySetting,
    updateCurrencySetting,
    enableCurrencySetting,
    getCurrencySetting,
    listCurrenciesLoading: loading,
    listCurrenciesRefetch: refetch,
  } = useCurrencyApi();

  useEffect(() => {
    if (tabName === "formats" || isSetup) {
      listCurrencyTableData({
        variables: {
          filter: { companyId: urlParams?.companyId },
          ...paginationInit,
        },
      });
    }
  }, [tabName, isSetup]);

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetch({
      limit: size,
      offset: size * (page - 1),
    });
  };

  const handleOnCancel = () => {
    setEditingKey("");
  };

  const isEditing = (record: Record<string, any>) => {
    return record.key === editingKey;
  };

  const handleOnEdit = async (record: Record<string, any>) => {
    const response = await getCurrencySetting({
      variables: {
        id: record?.id,
      },
    });
    const data = response?.data?.getCurrencySetting;
    form.setFields([
      {
        name: "decimalPlaces",
        value: data?.decimalPlaces,
      },
      {
        name: "isMainCurrency",
        value: !currency ? true : data?.isMainCurrency,
      },
      {
        name: "isActive",
        value: data?.isActive,
      },
    ]);

    setEditingKey(record?.key);
  };

  const handleOnSave = async () => {
    const successMutations: boolean[] = [];

    const handleErrors = (errors: any) => {
      if (errors && errors.length > 0) {
        errors.forEach((error: any) => {
          error.messages?.forEach((message: string) => {
            notification.error({
              message: t("Error"),
              description: message,
              placement: "bottomLeft",
            });
          });
        });
        return false;
      }
      return true;
    };

    const handleDisableCurrencySetting = async () => {
      const response = await disableCurrencySetting({ variables: { id: editingKey } });
      const errors = response?.data?.disableCurrencySetting?.errors;
      if (!handleErrors(errors)) {
        successMutations.push(false);
        return false;
      }
      refetch();
      setEditingKey("");
      return true;
    };

    const handleEnableCurrencySetting = async () => {
      const response = await enableCurrencySetting({ variables: { id: editingKey } });
      const success = response?.data?.enableCurrencySetting?.success;
      if (success) {
        refetch();
        return true;
      }
      return false;
    };

    const handleUpdateCurrencySetting = async () => {
      const response = await updateCurrencySetting({
        variables: {
          input: {
            id: editingKey,
            decimalPlaces: Number(decimalPlaces),
            isMainCurrency,
          },
        },
      });
      const errors = response?.data?.updateCurrencySetting?.errors;
      if (!handleErrors(errors)) {
        successMutations.push(false);
        return false;
      }
      refetch();
      return true;
    };

    if (!isActive) {
      await handleDisableCurrencySetting();
    } else {
      await handleEnableCurrencySetting();
    }

    await handleUpdateCurrencySetting();

    if (successMutations.length === 0) {
      setEditingKey("");
      notificationSuccessfullySaved(t("Currency"));
      refetchSettings!();
    }
  };

  const columns = useCurrencyTableColumns({
    handleOnSave,
    handleOnCancel,
    isEditing,
    handleOnEdit,
  });

  return (
    <Card
      title={<CardTitle icon={<FaCoins />} title={t("Currencies")} />}
      className="antd-newrai-card"
    >
      {!currency && (
        <>
          <AlertMessage message={t("Please select or set a main currency.")} type="error" />
          <br />
        </>
      )}
      <Form form={form} component={false}>
        <TableFilter
          className="antd-newrai-table"
          columns={columns}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            onChange: handlePagination,
            showSizeChanger: true,
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showTotal: total => t("Total table items", { count: total }),
          }}
        />
      </Form>
      <CreateCurrency refetch={refetch} refetchSettings={refetchSettings} />
    </Card>
  );
}
